import {
  buildWordBoundaryRegex,
  REWRITE_OPTION_LABEL_ALTERNATION,
  REWRITE_SIGNAL_TERMS,
} from "../../../../src/shared/rewriteSuggestionHeuristics";
import {
  sanitizeWriteCarouselSuggestionBody,
  carouselSuggestionLooksLikeWritingAdvice,
} from "../../../../src/shared/writeCarouselSuggestionSanitize";
import {
  isWriteRewriteCarouselTool,
  toolResultAllowsCarouselRender,
} from "../../../../src/shared/writeCarouselRenderGate";

export type ParsedRewriteSuggestions = {
  intro: string;
  suggestions: string[];
};

const REWRITE_SIGNAL_REGEX = buildWordBoundaryRegex(REWRITE_SIGNAL_TERMS);
const OPTION_SIGNAL_REGEX = new RegExp(
  `^\\s*(?:\\*\\*)?\\s*(?:${REWRITE_OPTION_LABEL_ALTERNATION})[ \\t]+\\d+\\b`,
  "im",
);
const NUMBERED_ITEM_REGEX = /^\s*(?:[-*]\s*)?(\d+)[.)]\s+/gm;
const OPTION_ITEM_REGEX = new RegExp(
  `^\\s*(?:[-*]\\s*)?(?:\\*\\*)?\\s*(?:${REWRITE_OPTION_LABEL_ALTERNATION})\\s+\\d+(?:\\s*\\([^)\n]{1,80}\\))?\\s*(?:\\*\\*)?\\s*:\\s*(?:\\*\\*)?\\s*`,
  "gim",
);
const BULLET_ITEM_REGEX = /^\s*[-*]\s+/gm;
const LABELLED_PARAGRAPH_REGEX =
  /^\s*(?:\*\*)?([A-Za-z][A-Za-z\s'/-]{2,80})(?::(?:\*\*)?|\*\*:)\s*(.+)$/;
const NON_SUGGESTION_LABEL_PREFIXES = [
  "here are",
  "the original",
  "note",
  "context",
  "why",
  "because",
];

function parseLabelledSuggestionParagraphs(source: string): ParsedRewriteSuggestions | null {
  const paragraphs = source
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (paragraphs.length < 2) return null;

  const introParts: string[] = [];
  const suggestions: string[] = [];

  for (const paragraph of paragraphs) {
    const [firstLine, ...restLines] = paragraph.split("\n");
    const labelMatch = LABELLED_PARAGRAPH_REGEX.exec(firstLine ?? "");
    if (!labelMatch) {
      if (suggestions.length === 0) introParts.push(paragraph);
      continue;
    }

    const label = labelMatch[1].trim().toLowerCase();
    if (NON_SUGGESTION_LABEL_PREFIXES.some((prefix) => label.startsWith(prefix))) {
      if (suggestions.length === 0) introParts.push(paragraph);
      continue;
    }

    const remainderFirstLine = labelMatch[2].trim();
    const body = [remainderFirstLine, ...restLines.map((line) => line.trim())]
      .join("\n")
      .trim()
      .replace(/^\*\*\s*/, "")
      .replace(/\s*\*\*$/, "")
      .replace(/^"(.*)"$/s, "$1");

    if (body) suggestions.push(sanitizeWriteCarouselSuggestionBody(body));
  }

  if (suggestions.length < 2) return null;
  return {
    intro: introParts.join("\n\n").trim(),
    suggestions,
  };
}

type ParseRewriteSuggestionOptions = {
  force?: boolean;
};

function finalizeParsedRewriteSuggestions(
  parsed: ParsedRewriteSuggestions | null,
): ParsedRewriteSuggestions | null {
  if (!parsed) return null;
  const suggestions = parsed.suggestions.filter(
    (entry) => !carouselSuggestionLooksLikeWritingAdvice(entry),
  );
  if (suggestions.length < 2) return null;
  return { ...parsed, suggestions };
}

type SuggestionItemPattern = {
  regex: RegExp;
  normalizeItem?: (item: string) => string;
};

const SUGGESTION_ITEM_PATTERNS: SuggestionItemPattern[] = [
  { regex: NUMBERED_ITEM_REGEX },
  { regex: OPTION_ITEM_REGEX, normalizeItem: normalizeQuotedSuggestion },
  { regex: BULLET_ITEM_REGEX, normalizeItem: normalizeQuotedSuggestion },
];

export function parseRewriteSuggestionsFromText(text: string, options: ParseRewriteSuggestionOptions = {}): ParsedRewriteSuggestions | null {
  if (typeof text !== "string") return null;
  const source = text.trim();
  if (!source) return null;
  if (!options.force && !REWRITE_SIGNAL_REGEX.test(source) && !OPTION_SIGNAL_REGEX.test(source)) return null;

  for (const { regex, normalizeItem } of SUGGESTION_ITEM_PATTERNS) {
    const parsedSuggestions = parseSuggestionsForPattern(source, regex, normalizeItem);
    if (parsedSuggestions) return finalizeParsedRewriteSuggestions(parsedSuggestions);
  }

  return finalizeParsedRewriteSuggestions(parseLabelledSuggestionParagraphs(source));
}

function parseSuggestionsForPattern(
  source: string,
  regex: RegExp,
  normalizeItem?: (item: string) => string,
): ParsedRewriteSuggestions | null {
  const matches = Array.from(source.matchAll(regex));
  return parseSuggestionsFromMatchBoundaries(source, matches, normalizeItem);
}

function parseSuggestionsFromMatchBoundaries(
  source: string,
  matches: RegExpMatchArray[],
  normalizeItem: (item: string) => string = (item) => item,
): ParsedRewriteSuggestions | null {
  if (matches.length < 2) return null;

  const intro = source.slice(0, matches[0].index ?? 0).trim();
  const suggestions: string[] = [];

  for (let i = 0; i < matches.length; i += 1) {
    const current = matches[i];
    const currentStart = (current.index ?? 0) + current[0].length;
    const nextStart = i + 1 < matches.length ? (matches[i + 1].index ?? source.length) : source.length;
    const item = normalizeItem(source.slice(currentStart, nextStart).trim());

    if (item) suggestions.push(sanitizeWriteCarouselSuggestionBody(item));
  }

  if (suggestions.length < 2) return null;
  return { intro, suggestions };
}

function normalizeQuotedSuggestion(item: string): string {
  return item
    .replace(/^\*\*\s*/, "")
    .replace(/\s*\*\*$/, "")
    .replace(/^"(.*)"$/s, "$1");
}

/** Pull carousel suggestions from persisted tool execution metadata. */
export function extractRewriteSuggestionsFromPersistedToolExecutions(
  toolExecutions: unknown,
): ParsedRewriteSuggestions | null {
  if (!Array.isArray(toolExecutions)) return null;

  for (let i = toolExecutions.length - 1; i >= 0; i -= 1) {
    const execution = toolExecutions[i] as { name?: unknown; result?: unknown };
    if (!isWriteRewriteCarouselTool(execution?.name)) continue;
    const result = execution.result;
    if (!result || typeof result !== "object") continue;
    if ((result as { renderAsCarousel?: unknown }).renderAsCarousel === false) continue;

    const ui = (result as {
      ui?: { type?: string; props?: { intro?: string; suggestions?: unknown; renderAsCarousel?: unknown } };
    }).ui;
    if (ui?.type === "RewriteSuggestionCarousel") {
      if (ui.props?.renderAsCarousel === false) continue;
      const suggestions = Array.isArray(ui.props?.suggestions)
        ? ui.props.suggestions
            .filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
            .map((entry) => sanitizeWriteCarouselSuggestionBody(entry))
            .filter((entry) => entry.length > 0 && !carouselSuggestionLooksLikeWritingAdvice(entry))
        : [];
      if (suggestions.length >= 1) {
        return {
          intro: typeof ui.props?.intro === "string" ? ui.props.intro : "",
          suggestions,
        };
      }
    }

    // Legacy + current write-tool payloads may store suggestions[] without carousel UI.
    const structuredSuggestions = (result as { suggestions?: unknown }).suggestions;
    if (Array.isArray(structuredSuggestions)) {
      const suggestions = structuredSuggestions
        .filter((entry): entry is string => typeof entry === "string" && entry.trim().length >= 8)
        .map((entry) => sanitizeWriteCarouselSuggestionBody(entry))
        .filter((entry) => entry.length >= 8 && !carouselSuggestionLooksLikeWritingAdvice(entry));
      if (suggestions.length >= 1) {
        return {
          intro: "",
          suggestions,
        };
      }
    }
  }

  return null;
}

/** True when persisted assistant metadata can render the rewrite carousel without body text. */
export function messageHasRewriteSuggestionCarouselMetadata(
  message: { metadata?: unknown } | null | undefined,
): boolean {
  const metadata = (message?.metadata ?? {}) as Record<string, unknown>;
  const rawSuggestions = metadata.rewriteSuggestions;
  if (rawSuggestions && typeof rawSuggestions === "object" && !Array.isArray(rawSuggestions)) {
    const suggestions = (rawSuggestions as { suggestions?: unknown }).suggestions;
    if (
      Array.isArray(suggestions)
      && suggestions.some((entry) => typeof entry === "string" && entry.trim().length > 0)
      && metadata.showRewriteSuggestionCarousel === true
    ) {
      return true;
    }
  }

  const toolExecutions = metadata.toolExecutions;
  if (Array.isArray(toolExecutions)) {
    return toolExecutions.some((toolExecution) =>
      toolResultAllowsCarouselRender({
        toolName: (toolExecution as { name?: unknown })?.name,
        result: (toolExecution as { result?: unknown })?.result,
      }),
    );
  }

  return false;
}
